import { driver } from "driver.js";
import { getClerkInstance } from "@/init/clerk";
import { getOnboardings } from "./onboardingsList.js";
import { TOUR_CONTEXTS, ROUTE_CONTEXT_MAP } from "./tourContexts.js";
import { app } from "@/main";
import { checkSelectorExists } from "./utils.js";
import { updateUserUnsafeMetadata } from "@/utils/userUtils.js";
import eventBus from "@/init/eventBus";
import i18n from "@/i18n/i18n";
import "driver.js/dist/driver.css";
import "@/assets/styles/driver.scss";

let onboardings = [];
let activeOnboardingCount = 0;
let activeDriver = null;
let destroyedByNav = false;
let isResetting = false;

const detachOnboarding = async (onboarding) => {
  onboarding?.abortController?.abort();
  const detachTrigger = onboarding?.detachTrigger;
  if (!detachTrigger) return;
  if (detachTrigger instanceof Promise) {
    const detach = await detachTrigger;
    if (typeof detach === "function") {
      detach();
    }
  } else if (typeof detachTrigger === "function") {
    detachTrigger();
  }
};

const detachAll = () => {
  return Promise.all(onboardings.map(detachOnboarding));
};

export const endAllOnboardings = async () => {
  await detachAll();
  const allKeys = Object.fromEntries(
    getOnboardings()
      .filter((onboarding) => onboarding.key)
      .map((onboarding) => [onboarding.key, true])
  );
  await updateUserUnsafeMetadata({ onboardings: allKeys });
};

const getDefaultOptions = () => ({
  disableActiveInteraction: true,
  showProgress: true,
  showButtons: ["next", "close"],
  doneBtnText: i18n.t("common.done"),
  nextBtnText: i18n.t("common.continue"),
  overlayOpacity: 0.25,
  stageRadius: 16,
  stagePadding: 12,
});

const updateUserMetadataWrapper = async (key) => {
  // Read current onboardings from Clerk at the time of writing — not from a
  // stale closure snapshot — to avoid overwriting keys set by concurrent tours.
  const current = getClerkInstance()?.user?.unsafeMetadata?.onboardings || {};
  await updateUserUnsafeMetadata({ onboardings: { ...current, [key]: true } });
};

const genOnNextClick = (key) => {
  return async (_element, _step, options) => {
    const activeIndex = options.driver.getActiveIndex();
    const totalSteps = options.driver.getConfig().steps.length;

    if (activeIndex === totalSteps - 1 && key) {
      updateUserMetadataWrapper(key);
    }

    options.driver.moveNext();
  };
};

const runOnboarding = async (onboarding) => {
  const { key, skipIfRunning } = onboarding;

  if (skipIfRunning && activeOnboardingCount > 0) return;

  const scenario =
    typeof onboarding.scenario === "function"
      ? onboarding.scenario()
      : onboarding.scenario;

  const clerkUser = getClerkInstance()?.user;
  const clerkUserOnboardings = clerkUser?.unsafeMetadata?.onboardings || {};

  if (key && !isResetting && clerkUserOnboardings[key]) {
    return;
  }

  // Wait for all required elements in parallel before starting the tour.
  // Each entry can be a selector string or a function returning a selector.
  if (onboarding.waitForElements?.length) {
    onboarding.abortController = new AbortController();
    const signal = onboarding.abortController.signal;

    try {
      await Promise.all(
        onboarding.waitForElements.map(async (selectorOrFn) => {
          const selector =
            typeof selectorOrFn === "function" ? selectorOrFn() : selectorOrFn;
          await checkSelectorExists(selector, { signal, timeout: 3000 }).catch(
            () => {}
          );
        })
      );
    } finally {
      onboarding.abortController = null;
    }
  }

  if (scenario?.steps?.[0]?.element) {
    onboarding.abortController = new AbortController();
    try {
      await checkSelectorExists(scenario.steps[0].element, {
        signal: onboarding.abortController.signal,
        timeout: 2000,
      });
    } catch {
      // element not found in time — filtering below will skip it
    } finally {
      onboarding.abortController = null;
    }
  }

  activeOnboardingCount++;

  const { forceFirstTime = false, ...scenarioOptions } = scenario || {};

  // First-time users have no quiz.version yet; after Guided Tour reset quiz.version persists
  const isFirstTimeUser = !clerkUser?.unsafeMetadata?.quiz?.version;
  const isForced = forceFirstTime && isFirstTimeUser;

  // Prevents onDestroyed from overwriting metadata already handled by End button or nav.
  let endHandled = false;
  destroyedByNav = false;

  let driverObj;

  const driverConfig = {
    ...getDefaultOptions(),
    onNextClick: genOnNextClick(key),
    onHighlightStarted: (_element, stepConfig) => {
      if (stepConfig.markKeys?.length) {
        stepConfig.markKeys.forEach((markKey) => {
          updateUserMetadataWrapper(markKey);
        });
      }
    },
    onPopoverRender: (popover) => {
      popover?.wrapper?.setAttribute("translate", "no");
      if (isForced) {
        return;
      }
      const endButton = document.createElement("button");
      endButton.style = "margin-right: auto; margin-left: 1rem;";
      endButton.innerText = i18n.t("common.finish");
      popover.footerButtons.prepend(endButton);
      endButton.addEventListener("click", async () => {
        endHandled = true;
        await endAllOnboardings();
        eventBus.$emit("onboarding:completed");
        driverObj?.destroy();
      });
    },
    onDestroyed: async () => {
      activeOnboardingCount = Math.max(0, activeOnboardingCount - 1);
      activeDriver = null;
      if (endHandled || !key) return;
      if (destroyedByNav) return;
      await updateUserMetadataWrapper(key);
      eventBus.$emit("onboarding:completed");
    },
    ...scenarioOptions,
  };

  // Filter steps whose elements are not in DOM so driver never renders them.
  // If a step's onNextClick was designed to prepare the immediate next step,
  // and that next step was filtered out, replace the custom onNextClick with a
  // plain moveNext so its side effects don't fire for the wrong step.
  const originalSteps = driverConfig.steps || [];
  const filteredSteps = originalSteps.filter((step) => {
    if (step.element && !document.querySelector(step.element)) {
      console.warn(
        `[onboarding] step element not found: "${step.element}" (tour key: "${key}")`
      );
      return false;
    }
    return true;
  });

  driverConfig.steps = filteredSteps.map((step, filteredIndex) => {
    const originalOnNextClick = step.popover?.onNextClick;
    if (!originalOnNextClick) return step;

    const originalIndex = originalSteps.indexOf(step);
    const originalNextStep = originalSteps[originalIndex + 1];
    const filteredNextStep = filteredSteps[filteredIndex + 1];

    // Original next step was removed — bypass custom logic, just advance.
    if (originalNextStep && originalNextStep !== filteredNextStep) {
      return {
        ...step,
        popover: {
          ...step.popover,
          onNextClick: (_element, _step, options) => {
            options.driver.moveNext();
          },
        },
      };
    }

    return step;
  });

  if (!driverConfig.steps.length) return;

  if (isForced) {
    driverConfig.allowClose = false;
    driverConfig.showButtons = ["next"];
  }

  driverObj = driver(driverConfig);
  activeDriver = driverObj;
  driverObj.drive();
};

const runOnboardings = async () => {
  const attachResults = onboardings.map((onboarding) => {
    const result = onboarding.trigger.attach(() => {
      runOnboarding(onboarding);
    });
    onboarding.detachTrigger = result;
    return result;
  });
  await Promise.all(attachResults.filter(Boolean));
};

const initOnboardings = async ({ router }) => {
  try {
    await Promise.all([
      import("./triggers/routeEventTrigger.js"),
      // import("./triggers/clickTrigger.js"),
      import("./triggers/vueEventTrigger.js"),
    ]);
  } catch (error) {
    console.error("Error initializing onboarding triggers", error);
  }
  onboardings = getOnboardings({ router });
  await runOnboardings();

  router.afterEach(() => {
    if (activeDriver) {
      destroyedByNav = true;
      activeDriver.destroy();
    }
  });
};

/**
 * Resets and re-runs all tours belonging to a specific context.
 * Clears Clerk metadata for context keys only,
 * then re-attaches all triggers (context tours will fire on next trigger).
 */
export const runTourContext = async (contextKey) => {
  const context = TOUR_CONTEXTS[contextKey];
  const keys = context?.keys || [];
  if (!keys.length) return;

  const current = getClerkInstance()?.user?.unsafeMetadata?.onboardings || {};

  const updated = { ...current };
  keys.forEach((key) => {
    delete updated[key];
  });

  const contextOnboardings = onboardings.filter((onboarding) =>
    keys.includes(onboarding.key)
  );
  await Promise.all(contextOnboardings.map(detachOnboarding));

  await updateUserUnsafeMetadata({ onboardings: updated });
  contextOnboardings.forEach((onboarding) => {
    onboarding.detachTrigger = onboarding.trigger.attach(() => {
      runOnboarding(onboarding);
    });
  });

  emitContextRestartEvents(contextKey);

  eventBus.$emit("onboarding:reset");
};

export const resetOnboardings = async () => {
  await detachAll();

  isResetting = true;
  await runOnboardings();

  const currentRouteName = app?.$router?.currentRoute?.name;
  const contextKey = ROUTE_CONTEXT_MAP[currentRouteName];
  if (contextKey) {
    emitContextRestartEvents(contextKey);
  }

  try {
    await updateUserUnsafeMetadata({
      onboardings: {},
    });
  } finally {
    isResetting = false;
  }
};

export const emitContextRestartEvents = (contextKey) => {
  const restartEvents = TOUR_CONTEXTS[contextKey]?.restartEvents || [];
  restartEvents.forEach((event) => eventBus.$emit(event));
};

export default initOnboardings;
